import { useCallback, useEffect, useMemo, useRef } from 'react';
import isNumber from 'lodash/isNumber';

import type { State as ImageState } from 'Lib/SmartImageLib';
import isNil from 'Lib/utils/isNil';

import { shouldSamplePerformance } from 'Store/performance/utils';

import type { TrackEvent } from 'Store/sparks/actions';
import { CLIENT_PERFORMANCE } from 'Store/sparks/sparks.events';

import ClickableDiv from 'Components/A11y/ClickableDiv';
import FocusedImage, {
  AUTOSIZE_FIT_HORIZONTAL,
  AUTOSIZE_FIT_VERTICAL,
} from 'Components/FocusedImage';
import type { CroppingAttributes } from 'Components/FocusedImage/types';

import useDisplaySize from './useDisplaySize';

type Props = {
  className?: string;
  shouldExcludeAriaLabel?: boolean;
  shouldTrackPerf?: boolean;
  'data-testid'?: string;
  onImageLoad: VoidFunction;
  width?: number;
  height?: number;
  trackEvent?: TrackEvent;
} & React.ImgHTMLAttributes<HTMLImageElement> &
  ImageState &
  CroppingAttributes;

const useImage = (props: Props) => {
  const {
    alt,
    'aria-hidden': ariaHidden,
    shouldExcludeAriaLabel,
    className,
    loading,
    draggable,
    height,
    style,
    intendedAspectRatio,
    interestLeft,
    interestTop,
    interestRight,
    interestBottom,
    interestZoom,
    onClick,
    onImageLoad,
    shouldTrackPerf,
    width,
    imgSrc = '',
    naturalHeight,
    naturalWidth,
    downloadTime,
    trackEvent,
    'data-testid': testId,
  } = props;

  const imgDivRef = useRef(null);
  const { displayWidth, displayHeight } = useDisplaySize(imgDivRef, { width, height });

  const trackPerformance = useCallback(() => {
    // trackEvent isn't defined when using unconnected component
    if (!imgSrc || !trackEvent) {
      return;
    }

    const uid = imgSrc.split('/').reverse()[1]; // User id is second-to-last part of url
    trackEvent(CLIENT_PERFORMANCE, {
      nsEndpoint: imgSrc.replace('https://', ''),
      nsMethod: 'GET',
      nsStatusCode: 200,
      durationInMillis: downloadTime,
      otherId: uid,
      payload: {
        imageWidth: naturalWidth,
        imageHeight: naturalHeight,
        url: imgSrc,
        type: imgSrc.split('.').pop(),
      },
    });
  }, [downloadTime, imgSrc, naturalHeight, naturalWidth, trackEvent]);

  useEffect(() => {
    if (shouldTrackPerf && shouldSamplePerformance()) {
      trackPerformance();
    }
  }, [shouldTrackPerf, trackPerformance]);

  const ariaLabel = shouldExcludeAriaLabel ? undefined : alt;

  const imageNode = useMemo(() => {
    // Render as the background image a div
    if (intendedAspectRatio === 'natural') {
      return (
        <ClickableDiv
          aria-hidden={ariaHidden}
          aria-label={ariaLabel}
          ref={imgDivRef}
          className={className}
          onClick={onClick}
          style={{ backgroundImage: `url(${imgSrc})` }}
          data-testid={testId}
        />
      );
    }

    if (isNumber(intendedAspectRatio)) {
      // It's possible for a background-image to display stretched if the background-size is set in
      // such a way that doesn't properly match the container size. Since the container size might
      // be affected by some CSS, the safest bet is to set only ONE background-size dimension and
      // allow the other one to autosize.
      //
      // We do this by comparing the actual display aspect ratio to the intendedAspectRatio prop.
      const displayAspectRatio = displayHeight && displayWidth ? displayWidth / displayHeight : 0;
      const autosizeFit =
        displayAspectRatio && displayAspectRatio < intendedAspectRatio
          ? AUTOSIZE_FIT_HORIZONTAL
          : AUTOSIZE_FIT_VERTICAL;

      return (
        <FocusedImage
          autosizeFit={autosizeFit}
          className={className}
          data-testid={testId}
          height={!isNil(displayHeight) ? displayHeight : height}
          innerRef={imgDivRef}
          interestBottom={interestBottom}
          interestLeft={interestLeft}
          interestRight={interestRight}
          interestTop={interestTop}
          interestZoom={interestZoom}
          naturalHeight={naturalHeight}
          naturalWidth={naturalWidth}
          onClick={onClick}
          src={imgSrc}
          style={style}
          width={!isNil(displayWidth) ? displayWidth : width}
        />
      );
    }

    return (
      <img
        loading={loading}
        ref={imgDivRef}
        alt={alt}
        aria-hidden={ariaHidden}
        className={className}
        draggable={draggable}
        height={height}
        onClick={onClick}
        onLoad={onImageLoad}
        src={imgSrc}
        width={width}
        data-testid={testId}
      />
    );
  }, [
    intendedAspectRatio,
    loading,
    alt,
    ariaHidden,
    className,
    draggable,
    height,
    onClick,
    onImageLoad,
    imgSrc,
    width,
    testId,
    displayHeight,
    displayWidth,
    interestBottom,
    interestLeft,
    interestRight,
    interestTop,
    interestZoom,
    naturalHeight,
    naturalWidth,
    style,
  ]);

  return {
    node: imageNode,
    ref: imgDivRef,
  };
};

export default useImage;
